import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import axios from 'axios';

import {
  Link,
} from 'react-router-dom';

import './ResultsFrameworkAdminPage.css';

/* global process */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const FRAMEWORKS_URL =
  `${API_BASE_URL}/api/v1/admin/result_frameworks`;

const countIndicators = (framework) =>
  framework.results.reduce(
    (total, result) =>
      total + result.indicators.length,
    0
  );

const ResultsFrameworkAdminPage = () => {
  const [frameworks, setFrameworks] =
    useState([]);

  const [status, setStatus] =
    useState('loading');

  const [error, setError] =
    useState('');

  useEffect(() => {
    const loadFrameworks = async () => {
      try {
        const response =
          await axios.get(
            FRAMEWORKS_URL
          );

        setFrameworks(response.data);
        setStatus('ready');
      } catch (requestError) {
        setError(
          requestError.response?.data?.error ||
          requestError.message ||
          'Unable to load results frameworks.'
        );

        setStatus('failed');
      }
    };

    loadFrameworks();
  }, []);

  const {
    activeFrameworks,
    archivedFrameworks,
    otherFrameworks,
  } = useMemo(() => {
    const active = [];
    const archived = [];
    const other = [];

    frameworks.forEach((framework) => {
      if (framework.status === 'active') {
        active.push(framework);
      } else if (
        framework.status === 'archived'
      ) {
        archived.push(framework);
      } else {
        other.push(framework);
      }
    });

    const sortFrameworks = (items) =>
      [...items].sort((a, b) => {
        if (a.start_year !== b.start_year) {
          return (
            (b.start_year || 0) -
            (a.start_year || 0)
          );
        }

        return (
          (a.pilar_id || 0) -
          (b.pilar_id || 0)
        );
      });

    return {
      activeFrameworks:
        sortFrameworks(active),

      archivedFrameworks:
        sortFrameworks(archived),

      otherFrameworks:
        sortFrameworks(other),
    };
  }, [frameworks]);

  const renderFrameworkRow = (
    framework,
    variant = 'active'
  ) => {
    const topLevelResults =
      framework.results.filter(
        (result) => !result.parent_id
      ).length;

    const indicators =
      countIndicators(framework);

    return (
      <article
        key={framework.id}
        className={
          `rf-index-row ${variant}`
        }
      >
        <div className="rf-index-identity">
          <span className="rf-index-pillar">
            Pillar {framework.pilar_id}
          </span>

          <span
            className={
              `rf-index-status ` +
              `${framework.status}`
            }
          >
            {framework.status}
          </span>

          <span className="rf-index-period">
            {framework.start_year}
            {' — '}
            {framework.end_year}
          </span>
        </div>

        <div className="rf-index-content">
          <h2>
            {framework.title_en}
          </h2>

          <p>
            {framework.description_en}
          </p>

          <div className="rf-index-metrics">
            <span>
              <strong>
                {topLevelResults}
              </strong>
              {' '}
              top-level results
            </span>

            <span>
              <strong>
                {framework.results.length}
              </strong>
              {' '}
              total results
            </span>

            <span>
              <strong>
                {indicators}
              </strong>
              {' '}
              indicators
            </span>
          </div>
        </div>

        <div className="rf-index-action">
          <Link
            to={
              `/admin/results-framework/` +
              `${framework.id}/edit`
            }
          >
            Edit framework
          </Link>
        </div>
      </article>
    );
  };

  if (status === 'loading') {
    return (
      <main className="admin-page">
        <p>
          Loading results framework...
        </p>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">
            Results management
          </p>

          <h1>Results Framework</h1>

          <p>
            Manage the current ACE results
            framework and preserve previous
            framework versions as an
            institutional archive.
          </p>
        </div>

        <Link
          to="/results-framework"
          className="admin-secondary-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          View public framework
        </Link>
      </header>

      {error && (
        <div className="rf-message error">
          {error}
        </div>
      )}

      <section className="rf-index-summary">
        <article>
          <span>Active frameworks</span>
          <strong>
            {activeFrameworks.length}
          </strong>
        </article>

        <article>
          <span>Archived frameworks</span>
          <strong>
            {archivedFrameworks.length}
          </strong>
        </article>

        <article>
          <span>Active results</span>
          <strong>
            {activeFrameworks.reduce(
              (total, framework) =>
                total +
                framework.results.length,
              0
            )}
          </strong>
        </article>

        <article>
          <span>Active indicators</span>
          <strong>
            {activeFrameworks.reduce(
              (total, framework) =>
                total +
                countIndicators(framework),
              0
            )}
          </strong>
        </article>
      </section>

      <section className="rf-index-section">
        <div className="rf-index-section-heading">
          <div>
            <p className="rf-index-eyebrow">
              Current strategic framework
            </p>

            <h2>
              Active framework — 2026–2030
            </h2>

            <p>
              These frameworks drive the
              current public Results Framework
              and ACE programme monitoring.
            </p>
          </div>
        </div>

        <div className="rf-index-list">
          {activeFrameworks.map(
            (framework) =>
              renderFrameworkRow(
                framework,
                'active'
              )
          )}
        </div>
      </section>

      {otherFrameworks.length > 0 && (
        <section className="rf-index-section">
          <div className="rf-index-section-heading">
            <div>
              <p className="rf-index-eyebrow">
                Work in progress
              </p>

              <h2>
                Draft frameworks
              </h2>
            </div>
          </div>

          <div className="rf-index-list">
            {otherFrameworks.map(
              (framework) =>
                renderFrameworkRow(
                  framework,
                  'draft'
                )
            )}
          </div>
        </section>
      )}

      <section className="rf-index-section archived">
        <div className="rf-index-section-heading">
          <div>
            <p className="rf-index-eyebrow">
              Institutional memory
            </p>

            <h2>
              Archived frameworks
            </h2>

            <p>
              Previous frameworks remain
              available for reference and
              historical continuity.
            </p>
          </div>

          <span className="rf-index-count">
            {archivedFrameworks.length}
          </span>
        </div>

        <div className="rf-index-list">
          {archivedFrameworks.map(
            (framework) =>
              renderFrameworkRow(
                framework,
                'archived'
              )
          )}
        </div>
      </section>
    </main>
  );
};

export default ResultsFrameworkAdminPage;
